import { all, call, put, takeEvery, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { uploadFailed } from 'src/components/alert/slice';
import { getRegionSetting } from 'src/selectors';
import { toSafeUploadName, uploadImageAndGetUrl } from 'src/util/upload';
import { cleanTexture } from 'src/services/sceneClient';
import { logError } from 'src/loggingManager';
import { getCylinderWarpById, getSceneSize } from 'src/selectors/layers';
import { getTransformsById } from 'src/selectors/transforms';
import { PERSPECTIVE_TRANSFORM, TUBE_TRANSFORM, PLACEMENT_TRANSFORM } from 'src/models/transforms';
import { CYLINDER_WARP } from 'src/models/documentWarps';
import {
    removeMask,
    uploadMask,
    maskUploaded,
    setWarp,
    replaceTransforms,
    updateCylinder,
    toggleFullWrap,
    updatePrecisionWrap,
    rotateCylinder,
    textureUploaded,
    removeTexture,
    uploadTexture,
} from './slice';
import { parseCylinderWarp, processTubeWarp } from 'src/util/cylinderWarp/tubeWarpTransform';

type CylinderWarpParams = {
    cylinderTop: {
        center: { x: number; y: number };
        radiusX: number;
        radiusY: number;
        rotationDegrees: number;
    };
    cylinderBottom: {
        center: { x: number; y: number };
        radiusX: number;
        radiusY: number;
        rotationDegrees: number;
    };
    decorationAreaDimensions: { width: number; height: number };
    productDiameter: number;
    decorationExtentDegreesAlpha: number;
    decorationExtentDegreesBeta: number;
    isDecorationRotated: boolean;
    imageDimensions: { width: number; height: number };
};

function createCylinderWarpParameters(width: number, height: number): CylinderWarpParams {
    const middleX = width / 2;
    const topY = height * 0.3;
    const bottomY = height * 0.7;
    const ellipseWidth = width * 0.2;
    const ellipseHeight = height * 0.05;

    const productDiameter = width * 0.5;
    const decorationAreaSize = (productDiameter * Math.PI) / 4;
    return {
        cylinderTop: {
            center: { x: middleX, y: topY },
            radiusX: ellipseWidth,
            radiusY: ellipseHeight,
            rotationDegrees: 0.0,
        },
        cylinderBottom: {
            center: { x: middleX, y: bottomY },
            radiusX: ellipseWidth,
            radiusY: ellipseHeight,
            rotationDegrees: 0.0,
        },
        decorationAreaDimensions: {
            width: decorationAreaSize,
            height: decorationAreaSize,
        },
        productDiameter,
        decorationExtentDegreesAlpha: 225,
        decorationExtentDegreesBeta: 90,
        isDecorationRotated: false,
        imageDimensions: { width, height },
    };
}

function* uploadAndAddMask({ payload }: PayloadAction<{ file: File; id: string }>): Generator<any, void, any> {
    const { file, id } = payload;
    const region = yield* select(getRegionSetting);
    const sceneSize = yield* select(getSceneSize);
    try {
        const url = (yield* call(uploadImageAndGetUrl as any, file, region, sceneSize)) as string;
        yield* put(maskUploaded({ url, id, fileName: toSafeUploadName(file.name) }));
    } catch (error: any) {
        logError(
            `unable to upload mask, there was an error while uploading/updating: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(uploadFailed(`Mask: ${error}`));
        yield* put(removeMask(id));
    }
}

function* uploadAndAddTexture({ payload }: PayloadAction<{ file: File; id: string }>): Generator<any, void, any> {
    const { file, id } = payload;
    const region = yield* select(getRegionSetting);
    try {
        const cleanedTexture = yield* call(cleanTexture, { texture: file });
        const url = yield* call(uploadImageAndGetUrl, cleanedTexture, region);
        yield* put(textureUploaded({ url, id, fileName: file.name }));
    } catch (error: any) {
        logError(
            `unable to upload texture, there was an error while uploading/updating: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(uploadFailed(`Texture: ${error}`));
        yield* put(removeTexture(id));
    }
}

function* uploadCylinderWarp(id: string): Generator<any, void, any> {
    const warp = yield* select(getCylinderWarpById, id);
    const documentTransforms = yield* select(getTransformsById, id);
    const perspectiveId = documentTransforms.find((transform: any) => transform.type === PERSPECTIVE_TRANSFORM)!.id;
    const cylinderId = documentTransforms.find((transform: any) => transform.type === TUBE_TRANSFORM)!.id;
    const placement = documentTransforms.find((transform: any) => transform.type === PLACEMENT_TRANSFORM);
    const placementId = placement ? placement.id : null;

    // TODO: Review this line of code
    const response: any = (parseCylinderWarp as any)(
        (processTubeWarp as any)(warp, id, perspectiveId, cylinderId, placementId),
    );

    if (response.perspectiveWarp && response.tubeWarp && response.documentSize) {
        const transforms = response.placementWarp
            ? [response.perspectiveWarp, response.tubeWarp, response.placementWarp]
            : [response.perspectiveWarp, response.tubeWarp];
        yield* put(
            replaceTransforms({
                id,
                transforms,
                width: response.documentSize.width,
                height: response.documentSize.height,
            }),
        );
    }
}

function* createCylinderWarp({
    payload,
}: PayloadAction<{ id: string; width: number; height: number; warpType: string }>): Generator<any, void, any> {
    const { id, width, height, warpType } = payload;
    if (warpType === CYLINDER_WARP) {
        const cylinder = createCylinderWarpParameters(width, height);
        yield* put(updateCylinder({ id, cylinder }));
    }
}

function* uploadPrecisionWrap({
    payload,
}: PayloadAction<{ id: string; width: number; diameter: number }>): Generator<any, void, any> {
    const { id, width, diameter } = payload;
    if (width > 0 && diameter > 0) {
        yield* call(uploadCylinderWarp, id);
    }
}

function* uploadFromId({ payload }: PayloadAction<string | { id: string }>): Generator<any, void, any> {
    const id = typeof payload === 'string' ? payload : payload.id;
    yield* call(uploadCylinderWarp, id);
}

export default function* documentsSaga() {
    return yield* all([
        yield* takeEvery(uploadMask, uploadAndAddMask),
        yield* takeEvery(setWarp, createCylinderWarp),
        yield* takeEvery(updateCylinder, uploadFromId),
        yield* takeEvery(toggleFullWrap, uploadFromId),
        yield* takeEvery(rotateCylinder, uploadFromId),
        yield* takeEvery(updatePrecisionWrap, uploadPrecisionWrap),
        yield* takeEvery(uploadTexture, uploadAndAddTexture),
    ]);
}
